import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useDocumentTitle } from "../lib/useDocumentTitle";
import Header from "../components/app/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { STATUS_STYLES } from "../lib/phases";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Shield, Home, Inbox, Sliders, Trash2, ArrowUpRight, Save, RotateCcw, ShieldOff, Send } from "lucide-react";

const TABS = [
  { id: "listings", label: "All Listings", icon: Home },
  { id: "leads", label: "Seller Leads", icon: Inbox },
  { id: "settings", label: "Settings", icon: Sliders },
];

function StatusBadge({ status }) {
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${STATUS_STYLES[status] || STATUS_STYLES["Pre-Listing"]}`}>
      {status}
    </span>
  );
}

function AdminListings({ listings, onDelete }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    if (!q.trim()) return listings;
    const s = q.toLowerCase();
    return listings.filter((l) =>
      (l.property_address || "").toLowerCase().includes(s) ||
      (l.seller_names || "").toLowerCase().includes(s) ||
      (l.seller_email || "").toLowerCase().includes(s) ||
      (l.listing_agent || "").toLowerCase().includes(s)
    );
  }, [listings, q]);

  return (
    <div>
      <div className="mb-4">
        <Input
          data-testid="admin-listings-search"
          placeholder="Search by address, seller, agent, email..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="h-10 rounded-full bg-white border-stone-300"
        />
      </div>
      <div className="overflow-hidden rounded-md border border-stone-200 bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-stone-200 bg-stone-50 text-left text-[11px] uppercase tracking-widest text-stone-500">
              <th className="px-4 py-3 font-medium">Property</th>
              <th className="px-4 py-3 font-medium">Seller</th>
              <th className="px-4 py-3 font-medium">Agent</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium text-right">Progress</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-stone-500 italic">
                  No listings match.
                </td>
              </tr>
            ) : (
              filtered.map((l) => {
                const total = Object.keys(l.tasks || {}).length ? Object.values(l.tasks || {}).filter((t) => t.done).length : 0;
                const doneAll = Object.values(l.tasks || {}).filter((t) => t.done).length;
                return (
                  <tr key={l.id} data-testid={`admin-listing-row-${l.id}`} className="border-b border-stone-100 last:border-0 hover:bg-stone-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-stone-900">{l.property_address || "Untitled"}</div>
                      <div className="text-xs text-stone-500 font-mono">{l.mls_number || "—"}</div>
                    </td>
                    <td className="px-4 py-3 text-stone-700">
                      <div>{l.seller_names || "—"}</div>
                      <div className="text-xs text-stone-500">{l.seller_email || ""}</div>
                    </td>
                    <td className="px-4 py-3 text-stone-700">{l.listing_agent || "—"}</td>
                    <td className="px-4 py-3"><StatusBadge status={l.listing_status} /></td>
                    <td className="px-4 py-3 text-right tabular-nums text-stone-700">{doneAll}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-3">
                        <Link
                          to={`/listing/${l.id}`}
                          data-testid={`admin-listing-open-${l.id}`}
                          className="inline-flex items-center gap-1 text-[11px] font-medium text-[#010082] hover:underline"
                        >
                          Open
                          <ArrowUpRight className="h-3 w-3" />
                        </Link>
                        <button
                          onClick={() => onDelete(l)}
                          data-testid={`admin-listing-delete-${l.id}`}
                          className="text-stone-400 hover:text-rose-600 transition-colors"
                          aria-label="Delete listing"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AdminLeads({ leads, onStatusChange, onDelete }) {
  return (
    <div className="overflow-hidden rounded-md border border-stone-200 bg-white">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-stone-200 bg-stone-50 text-left text-[11px] uppercase tracking-widest text-stone-500">
            <th className="px-4 py-3 font-medium">Name</th>
            <th className="px-4 py-3 font-medium">Contact</th>
            <th className="px-4 py-3 font-medium">Property</th>
            <th className="px-4 py-3 font-medium">Timeline</th>
            <th className="px-4 py-3 font-medium">Received</th>
            <th className="px-4 py-3 font-medium">Status</th>
            <th className="px-4 py-3"></th>
          </tr>
        </thead>
        <tbody>
          {leads.length === 0 ? (
            <tr>
              <td colSpan={7} className="px-4 py-12 text-center text-stone-500 italic">
                No seller leads yet.
              </td>
            </tr>
          ) : (
            leads.map((lead) => (
              <tr key={lead.id} data-testid={`admin-lead-row-${lead.id}`} className="border-b border-stone-100 last:border-0 align-top">
                <td className="px-4 py-3 font-medium text-stone-900">{lead.name}</td>
                <td className="px-4 py-3 text-stone-700">
                  <div>{lead.email}</div>
                  {lead.phone && <div className="text-xs text-stone-500">{lead.phone}</div>}
                </td>
                <td className="px-4 py-3 text-stone-700">
                  <div>{lead.address || "—"}</div>
                  {lead.message && (
                    <div className="text-xs text-stone-500 mt-1 italic line-clamp-2 max-w-[240px]">"{lead.message}"</div>
                  )}
                </td>
                <td className="px-4 py-3 text-stone-700 text-xs">{lead.timeline || "—"}</td>
                <td className="px-4 py-3 text-stone-500 text-xs whitespace-nowrap">
                  {new Date(lead.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                </td>
                <td className="px-4 py-3">
                  <select
                    data-testid={`admin-lead-status-${lead.id}`}
                    value={lead.status || "new"}
                    onChange={(e) => onStatusChange(lead.id, e.target.value)}
                    className="h-8 rounded-md border border-stone-300 bg-white px-2 text-xs"
                  >
                    <option value="new">New</option>
                    <option value="contacted">Contacted</option>
                    <option value="won">Won</option>
                    <option value="lost">Lost</option>
                  </select>
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => onDelete(lead)}
                    data-testid={`admin-lead-delete-${lead.id}`}
                    className="text-stone-400 hover:text-rose-600 transition-colors"
                    aria-label="Delete lead"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [items, setItems] = useState({});
  const [busy, setBusy] = useState(false);
  const [firing, setFiring] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/settings");
        setSettings(data);
        setItems(data.seller_items || {});
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load settings");
      }
    })();
  }, []);

  const fireDailyNow = async () => {
    setFiring(true);
    try {
      const { data } = await api.post("/admin/reminders/run-daily-seller-emails");
      if (data.disabled) {
        toast.error("Daily loop is disabled — turn it on first.");
      } else {
        toast.success(`Sent ${data.sent} · skipped ${data.skipped}`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send");
    } finally {
      setFiring(false);
    }
  };

  const fireWeeklyNow = async () => {
    setFiring(true);
    try {
      const { data } = await api.post("/admin/reminders/run-weekly-seller-emails");
      if (data.disabled) {
        toast.error("Seller loop is disabled — turn it on first.");
      } else {
        toast.success(`Weekly sent ${data.sent} · skipped ${data.skipped}`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send");
    } finally {
      setFiring(false);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/settings", {
        seller_daily_loop_enabled: !!settings.seller_daily_loop_enabled,
        closing_email_enabled: settings.closing_email_enabled !== false,
        pleasure_email_enabled: settings.pleasure_email_enabled !== false,
        signing_reminder_enabled: settings.signing_reminder_enabled !== false,
        agent_overdue_email_enabled: settings.agent_overdue_email_enabled !== false,
        broker_dre_license: settings.broker_dre_license || "",
        seller_items: items,
      });
      setSettings({ ...settings, ...data });
      toast.success("Settings saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const updateItem = (tid, key, value) => {
    setItems((prev) => ({ ...prev, [tid]: { ...(prev[tid] || {}), [key]: value } }));
  };

  const restoreDefault = async () => {
    try {
      const { data } = await api.put("/admin/settings", {
        seller_daily_loop_enabled: !!settings?.seller_daily_loop_enabled,
        closing_email_enabled: settings?.closing_email_enabled !== false,
        pleasure_email_enabled: settings?.pleasure_email_enabled !== false,
        signing_reminder_enabled: settings?.signing_reminder_enabled !== false,
        agent_overdue_email_enabled: settings?.agent_overdue_email_enabled !== false,
        broker_dre_license: settings?.broker_dre_license || "",
        seller_items: null,
      });
      // Reload from server to get defaults
      const { data: fresh } = await api.get("/admin/settings");
      setSettings(fresh);
      setItems(fresh.seller_items || {});
      toast.success("Restored default copy");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Restore failed");
    }
  };

  if (!settings) {
    return <div className="text-stone-500 italic">Loading settings…</div>;
  }

  return (
    <div className="space-y-8">
      <div className="rounded-md border p-6 flex items-start justify-between gap-4"
        style={{ borderColor: "rgba(1,0,130,0.2)", background: "rgba(1,0,130,0.03)" }}
      >
        <div>
          <p className="overline text-stone-500 mb-2">Manual trigger</p>
          <p className="text-sm font-medium text-stone-900">Send today's digest to all sellers now</p>
          <p className="text-xs text-stone-500 mt-1 leading-relaxed max-w-md">
            Fires the 6 PM email job immediately for every eligible home. Sellers who already got today's email are skipped automatically.
          </p>
        </div>
        <Button
          onClick={fireDailyNow}
          disabled={firing}
          data-testid="admin-fire-daily-now"
          className="rounded-full px-4 text-white shrink-0"
          style={{ backgroundColor: "#010082" }}
        >
          <Send className="h-3.5 w-3.5 mr-1.5" />
          {firing ? "Sending..." : "Fire now"}
        </Button>
      </div>

      <div className="rounded-md border p-6 flex items-start justify-between gap-4"
        style={{ borderColor: "rgba(1,0,130,0.2)", background: "rgba(1,0,130,0.03)" }}
      >
        <div>
          <p className="overline text-stone-500 mb-2">Manual trigger</p>
          <p className="text-sm font-medium text-stone-900">Send the Sunday 5 PM PT weekly recap now</p>
          <p className="text-xs text-stone-500 mt-1 leading-relaxed max-w-md">
            Fires the weekly summary for every eligible home covering the past 7 days. Sellers who already got this week's recap are skipped automatically.
          </p>
        </div>
        <Button
          onClick={fireWeeklyNow}
          disabled={firing}
          data-testid="admin-fire-weekly-now"
          className="rounded-full px-4 text-white shrink-0"
          style={{ backgroundColor: "#010082" }}
        >
          <Send className="h-3.5 w-3.5 mr-1.5" />
          {firing ? "Sending..." : "Fire weekly"}
        </Button>
      </div>

      <div className="rounded-md border border-stone-200 bg-white p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="overline text-stone-500 mb-2">6 PM daily emails</p>
            <p className="text-sm font-medium text-stone-900">Seller daily digest loop</p>
            <p className="text-xs text-stone-500 mt-1 leading-relaxed max-w-md">
              When on, sellers receive an email at 6 PM Pacific summarizing tasks completed and notes logged today. Turn off to pause across every home globally.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {!settings.seller_daily_loop_enabled && <ShieldOff className="h-4 w-4 text-rose-500" />}
            <Switch
              data-testid="admin-loop-toggle"
              checked={!!settings.seller_daily_loop_enabled}
              onCheckedChange={(v) => setSettings((s) => ({ ...s, seller_daily_loop_enabled: v }))}
            />
          </div>
        </div>
      </div>

      <div className="rounded-md border border-stone-200 bg-white p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="overline text-stone-500 mb-2">On close</p>
            <p className="text-sm font-medium text-stone-900">Auto-fire closing "You've closed" email</p>
            <p className="text-xs text-stone-500 mt-1 leading-relaxed max-w-md">
              When a home closes (task "Confirm loan approval, funding and recording" is marked done, or you change status to Closed), automatically send the branded congratulations + referral ask email.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {settings.closing_email_enabled === false && <ShieldOff className="h-4 w-4 text-rose-500" />}
            <Switch
              data-testid="admin-closing-toggle"
              checked={settings.closing_email_enabled !== false}
              onCheckedChange={(v) => setSettings((s) => ({ ...s, closing_email_enabled: v }))}
            />
          </div>
        </div>
      </div>

      <div className="rounded-md border border-stone-200 bg-white p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="overline text-stone-500 mb-2">Daily · 6 PM PT</p>
            <p className="text-sm font-medium text-stone-900">Agent overdue digest</p>
            <p className="text-xs text-stone-500 mt-1 leading-relaxed max-w-md">
              Emails each agent a daily rollup of every overdue task across their listings, grouped by home. Keeps firing until everything is cleared or the target dates are updated. Sellers never see this.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {settings.agent_overdue_email_enabled === false && <ShieldOff className="h-4 w-4 text-rose-500" />}
            <Switch
              data-testid="admin-agent-overdue-toggle"
              checked={settings.agent_overdue_email_enabled !== false}
              onCheckedChange={(v) => setSettings((s) => ({ ...s, agent_overdue_email_enabled: v }))}
            />
          </div>
        </div>
      </div>

      <div className="rounded-md border border-stone-200 bg-white p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="overline text-stone-500 mb-2">7 days after close</p>
            <p className="text-sm font-medium text-stone-900">"It was a pleasure" postscript</p>
            <p className="text-xs text-stone-500 mt-1 leading-relaxed max-w-md">
              A warm follow-up sent one week after closing — a personal thank-you and an invitation to refer friends. Turn off to skip the postscript across every home.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {settings.pleasure_email_enabled === false && <ShieldOff className="h-4 w-4 text-rose-500" />}
            <Switch
              data-testid="admin-pleasure-toggle"
              checked={settings.pleasure_email_enabled !== false}
              onCheckedChange={(v) => setSettings((s) => ({ ...s, pleasure_email_enabled: v }))}
            />
          </div>
        </div>
      </div>

      <div className="rounded-md border border-stone-200 bg-white p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <p className="overline text-stone-500 mb-2">Broker</p>
            <p className="text-sm font-medium text-stone-900">DRE license number</p>
            <p className="text-xs text-stone-500 mt-1 leading-relaxed max-w-md">
              Printed on every generated Commission Demand PDF, email body, and print preview. Leave blank to fall back to <code className="text-stone-600">BROKER_DRE_LICENSE</code> env var.
            </p>
          </div>
          <Input
            data-testid="admin-broker-dre"
            value={settings.broker_dre_license || ""}
            onChange={(e) => setSettings((s) => ({ ...s, broker_dre_license: e.target.value }))}
            placeholder="01234567"
            className="h-10 max-w-[220px] bg-white border-stone-300 font-mono text-sm shrink-0"
          />
        </div>
      </div>

      <div className="rounded-md border border-stone-200 bg-white p-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <p className="overline text-stone-500 mb-2">Seller email · "Coming up" copy</p>
            <p className="text-sm font-medium text-stone-900">What to send sellers about upcoming tasks</p>
            <p className="text-xs text-stone-500 mt-1 leading-relaxed max-w-md">
              Edit the label and helper text for each seller-facing item in the daily digest.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={restoreDefault}
            data-testid="admin-restore-defaults"
            className="rounded-full border-stone-300 text-xs"
          >
            <RotateCcw className="h-3 w-3 mr-1.5" />
            Restore defaults
          </Button>
        </div>
        <div className="space-y-5">
          {Object.entries(items).map(([tid, meta]) => (
            <div key={tid} className="grid grid-cols-1 sm:grid-cols-12 gap-3 pb-5 border-b border-stone-100 last:border-0">
              <div className="sm:col-span-2">
                <p className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">{tid}</p>
              </div>
              <div className="sm:col-span-10 space-y-2">
                <div>
                  <Label className="text-[11px] text-stone-500">Label</Label>
                  <Input
                    value={meta.label || ""}
                    onChange={(e) => updateItem(tid, "label", e.target.value)}
                    className="h-9 text-sm border-stone-300 bg-white"
                    data-testid={`admin-item-label-${tid}`}
                  />
                </div>
                <div>
                  <Label className="text-[11px] text-stone-500">Note</Label>
                  <Textarea
                    value={meta.note || ""}
                    onChange={(e) => updateItem(tid, "note", e.target.value)}
                    rows={2}
                    className="text-sm border-stone-300 bg-white"
                    data-testid={`admin-item-note-${tid}`}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          onClick={save}
          disabled={busy}
          data-testid="admin-save-settings"
          className="rounded-full px-5 text-white"
          style={{ backgroundColor: "#010082" }}
        >
          <Save className="h-3.5 w-3.5 mr-1.5" />
          {busy ? "Saving..." : "Save all settings"}
        </Button>
      </div>
    </div>
  );
}

export default function Admin() {
  useDocumentTitle();
  const nav = useNavigate();
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("listings");
  const [stats, setStats] = useState(null);
  const [emailStats, setEmailStats] = useState(null);
  const [listings, setListings] = useState([]);
  const [leads, setLeads] = useState([]);
  const [deleteListing, setDeleteListing] = useState(null);
  const [deleteLead, setDeleteLead] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, l, ld, es] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/listings"),
        api.get("/leads"),
        api.get("/admin/email-stats"),
      ]);
      setStats(s.data);
      setListings(l.data);
      setLeads(ld.data);
      setEmailStats(es.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load admin data");
    }
  }, []);

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) {
      nav("/app", { replace: true });
      return;
    }
    if (user?.role === "admin") load();
  }, [user, loading, nav, load]);

  const handleListingDelete = async () => {
    if (!deleteListing) return;
    try {
      await api.delete(`/admin/listings/${deleteListing.id}`);
      setListings((prev) => prev.filter((x) => x.id !== deleteListing.id));
      toast.success("Listing removed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    } finally {
      setDeleteListing(null);
    }
  };

  const handleLeadStatus = async (leadId, status) => {
    try {
      const { data } = await api.patch(`/admin/leads/${leadId}`, { status });
      setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, ...data } : l)));
      toast.success("Lead updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    }
  };

  const handleLeadDelete = async () => {
    if (!deleteLead) return;
    try {
      await api.delete(`/admin/leads/${deleteLead.id}`);
      setLeads((prev) => prev.filter((x) => x.id !== deleteLead.id));
      toast.success("Lead removed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    } finally {
      setDeleteLead(null);
    }
  };

  if (loading || !user || user.role !== "admin") return null;

  return (
    <div className="min-h-screen bg-[#faf7f2]">
      <Header />
      <main className="mx-auto max-w-7xl px-6 py-12 lg:py-16">
        <div className="mb-10">
          <div className="flex items-center gap-2 text-stone-500 mb-3">
            <Shield className="h-3.5 w-3.5" />
            <p className="overline">Admin</p>
          </div>
          <h1 className="text-4xl sm:text-5xl font-heading font-medium tracking-tight text-stone-900 leading-[1.05]">
            Command center.
          </h1>
          <p className="text-sm text-stone-500 mt-3 max-w-lg">
            Every listing, seller lead, and system-wide setting — in one place.
          </p>
        </div>

        {stats && (
          <div data-testid="admin-stats" className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
            {[
              { label: "Total homes", value: stats.listings },
              { label: "Active", value: stats.listings_active },
              { label: "Closed", value: stats.listings_closed },
              { label: "New leads", value: stats.leads_new },
            ].map((s) => (
              <div key={s.label} className="rounded-md border border-stone-200 bg-white p-5">
                <p className="overline text-stone-500 mb-2">{s.label}</p>
                <p className="text-3xl font-heading font-medium text-stone-900 tabular-nums">{s.value}</p>
              </div>
            ))}
          </div>
        )}

        {emailStats && (
          <div data-testid="admin-email-stats" className="rounded-md border border-stone-200 bg-white p-6 mb-10">
            <div className="flex items-baseline justify-between gap-3 mb-4">
              <div>
                <p className="overline text-stone-500 mb-1">Email engagement · all time</p>
                <p className="text-xl font-heading font-semibold text-stone-900 tracking-tight">
                  Are sellers actually reading the digest?
                </p>
              </div>
              <p className="text-[11px] text-stone-500 italic hidden sm:block">
                Opens are approximate. Clicks are exact.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {["daily", "weekly"].map((kind) => {
                const d = emailStats[kind] || {};
                return (
                  <div key={kind} data-testid={`admin-email-${kind}`} className="rounded-md border border-stone-200 p-4">
                    <p className="overline text-stone-500 mb-3">{kind === "daily" ? "Daily digest" : "Weekly recap"}</p>
                    <div className="grid grid-cols-4 gap-2">
                      <div>
                        <p className="text-2xl font-heading font-medium text-stone-900 tabular-nums">{d.sends || 0}</p>
                        <p className="text-[10px] uppercase tracking-widest text-stone-500 mt-1">Sends</p>
                      </div>
                      <div>
                        <p className="text-2xl font-heading font-medium text-stone-900 tabular-nums">{d.opens || 0}</p>
                        <p className="text-[10px] uppercase tracking-widest text-stone-500 mt-1">Opens</p>
                      </div>
                      <div>
                        <p className="text-2xl font-heading font-medium text-stone-900 tabular-nums">{d.clicks || 0}</p>
                        <p className="text-[10px] uppercase tracking-widest text-stone-500 mt-1">Clicks</p>
                      </div>
                      <div>
                        <p className="text-2xl font-heading font-medium text-stone-900 tabular-nums">
                          {d.open_rate || 0}<span className="text-sm text-stone-400">%</span>
                        </p>
                        <p className="text-[10px] uppercase tracking-widest text-stone-500 mt-1">Open rate</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="mb-6 border-b border-stone-200 flex gap-1">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              data-testid={`admin-tab-${id}`}
              className={`px-4 py-2.5 -mb-px inline-flex items-center gap-2 text-sm font-medium border-b-2 transition-colors ${
                tab === id
                  ? "border-[#010082] text-[#010082]"
                  : "border-transparent text-stone-500 hover:text-stone-900"
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
              {id === "leads" && stats?.leads_new > 0 && (
                <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-4 rounded-full text-[10px] font-semibold text-white px-1" style={{ backgroundColor: "#010082" }}>
                  {stats.leads_new}
                </span>
              )}
            </button>
          ))}
        </div>

        {tab === "listings" && <AdminListings listings={listings} onDelete={setDeleteListing} />}
        {tab === "leads" && (
          <AdminLeads leads={leads} onStatusChange={handleLeadStatus} onDelete={setDeleteLead} />
        )}
        {tab === "settings" && <AdminSettings />}
      </main>

      <AlertDialog open={!!deleteListing} onOpenChange={(o) => !o && setDeleteListing(null)}>
        <AlertDialogContent data-testid="admin-delete-listing-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading tracking-tight">Remove listing?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove {deleteListing?.property_address || "this home"} and all its task data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleListingDelete} className="bg-rose-600 hover:bg-rose-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteLead} onOpenChange={(o) => !o && setDeleteLead(null)}>
        <AlertDialogContent data-testid="admin-delete-lead-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading tracking-tight">Remove lead?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the lead from {deleteLead?.name || "this contact"}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleLeadDelete} className="bg-rose-600 hover:bg-rose-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
